import { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Minus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOUR_MS = 60 * 60 * 1000;
const STALE_MS = 48 * HOUR_MS;

function formatAge(then) {
  const diff = Date.now() - new Date(then).getTime();
  if (diff < HOUR_MS) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 36 * HOUR_MS) return `${Math.round(diff / HOUR_MS)}h ago`;
  return `${Math.round(diff / (24 * HOUR_MS))}d ago`;
}

/**
 * TierResyncStatusBadge — Round P108av+5 (Feb 2026).
 *
 * Renders a compact status pill for the Streaks admin group header,
 * signalling whether the daily 9 AM tier-resync cron has run recently:
 *   ✓  green  — last run < 48h ago (both cron + manual button count)
 *   ⚠  red    — last run > 48h ago OR last run failed
 *   —  gray   — never run yet (or admin endpoint 404)
 *
 * Polls `/admin/tiers/resync-status` on mount + every 5 minutes.
 * Admin-only.
 */
export default function TierResyncStatusBadge() {
  const { user } = useAuth();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/tiers/resync-status`, { withCredentials: true });
        if (!cancelled) setState(data?.last_run || null);
      } catch {
        if (!cancelled) setState(null);
      }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (user?.role !== 'admin') return null;

  // Never run
  if (!state?.finished_at) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"
        title="The daily 9 AM ET tier-resync cron has never run yet. First run will happen next weekday morning."
        data-testid="tier-resync-badge-never"
      >
        <Minus className="w-3 h-3" /> never
      </span>
    );
  }

  const failed = state.status === 'failed';
  const ageMs = Date.now() - new Date(state.finished_at).getTime();
  const stale = ageMs > STALE_MS;
  const age = formatAge(state.finished_at);

  if (failed) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] text-rose-700 dark:text-rose-300 font-bold"
        title={`Last tier-resync FAILED at ${state.finished_at}${state.error ? `\n\nError: ${state.error}` : ''}\n\nClick "Resync Tier Labels" below to retry.`}
        data-testid="tier-resync-badge-failed"
      >
        <AlertTriangle className="w-3 h-3" /> failed {age}
      </span>
    );
  }

  if (stale) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] text-rose-700 dark:text-rose-300 font-bold"
        title={`The daily 9 AM tier-resync hasn't run in over 48 hours (last: ${state.finished_at}). This usually means APScheduler was down. Check the cron history or click "Resync Tier Labels" below to force a run.`}
        data-testid="tier-resync-badge-stale"
      >
        <AlertTriangle className="w-3 h-3" /> stale {age}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 dark:text-emerald-300 font-bold"
      title={`Tier resync succeeded ${age} (${state.finished_at})${state.source === 'manual_button' ? ' — via manual button click' : ' — via 9 AM daily cron'}`}
      data-testid="tier-resync-badge-ok"
    >
      <CheckCircle2 className="w-3 h-3" /> {age}
    </span>
  );
}
